import PropTypes from 'prop-types';
import { HelpCircle } from 'lucide-react';

import { cn } from '#/lib/utils';
import { Textarea } from '#/modules/commonComponents/form/Textarea';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '#/modules/commonComponents/tooltip';

import { getNestedError, hasRegisterFn, noop } from './formFieldUtils';

const FloatingTextarea = ({
  containerClassName,
  textareaClassName,
  label,
  tip,
  id,
  value = '',
  register = noop,
  errors,
  onChange = noop,
  disabled = false,
  readOnly = false,
  rows,
}) => {
  const hasRegister = hasRegisterFn(register);
  const registerProps = hasRegister ? register(id) : {};
  const isControlled = !hasRegister && value !== undefined && value !== null && onChange !== noop;
  const isReadOnly = !hasRegister && value !== undefined && value !== null && onChange === noop;
  const fieldError = getNestedError(errors, id);

  const controlProps = hasRegister
    ? { ...registerProps, id, readOnly, disabled }
    : isControlled
      ? {
        id, value: value || '', onChange, readOnly, disabled,
      }
      : isReadOnly
        ? {
          id, value: value || '', readOnly: readOnly !== false, disabled,
        }
        : {
          id, defaultValue: value || '', onChange, readOnly, disabled,
        };

  return (
    <div className={cn('textarea_container', containerClassName)}>
      <div className="relative">
        <Textarea
          className={cn('textarea min-h-25', textareaClassName, readOnly && 'readonly')}
          placeholder=" "
          rows={rows}
          aria-invalid={fieldError ? true : undefined}
          {...controlProps}
        />
        <label htmlFor={id} className="textarea_label">
          {tip ? (
            <span className="inline-flex items-center gap-0.5">
              <span>{label}</span>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="
                      text-gray-400 inline-flex cursor-help items-center border-0 bg-transparent p-0
                      hover:text-primary-600
                    "
                    aria-label={`${label}說明`}
                    onClick={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                    onMouseDown={(event) => {
                      event.preventDefault();
                      event.stopPropagation();
                    }}
                  >
                    <HelpCircle size={14} />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top" className="max-w-xs">
                  {tip}
                </TooltipContent>
              </Tooltip>
            </span>
          ) : label}
        </label>
      </div>
      <p className="form_error">
        {fieldError ? fieldError.message || null : null}
      </p>
    </div>
  );
};

FloatingTextarea.propTypes = {
  containerClassName: PropTypes.string,
  textareaClassName: PropTypes.string,
  label: PropTypes.string.isRequired,
  tip: PropTypes.string,
  id: PropTypes.string,
  value: PropTypes.string,
  register: PropTypes.func,
  errors: PropTypes.object,
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  rows: PropTypes.number,
};

export default FloatingTextarea;
